import api from "@/api";
import { defineStore } from "pinia";
import { School } from "@/interfaces/school.interface";
import {
  InitialSchoolStructure,
  SchoolStructure,
} from "@/interfaces/school-structure.interface";
import { Locale } from "@/interfaces/locale.interface";
import { get } from "lodash";
import { Admin } from "@/interfaces/admin.interface";
import { useFilterStore } from "./filterStore";
import { resolveSchoolGrammarEnabled } from "@/utils/grammar-gate";
import { useDataStore } from "./dataStore";
import { useLearnerStore } from "./learnerStore";
import moment from "moment";
import i18n from "@/plugins/i18n";
import { getSchoolCreationErrorMessage } from "@/lib/school-creation-error";

export const useSchoolStore = defineStore({
  id: "schoolStore",
  state: () => ({
    loading: false,
    updateLoader: false,
    localeLoader: false,
    schoolCreationLoader: false,
    schoolCreationError: "",
    schoolStructureLoader: false,
    currentSchool: null as School | null,
    locales: [] as Locale[],
    currentSchoolStructure: null as SchoolStructure | null,
    initialSchoolStructure: {
      id: "",
      teachers: [],
      classGroups: [],
    } as InitialSchoolStructure | null,
    step: 0,
    SelectedGrades: [],
    enableSentModal: false,
  }),
  getters: {
    getLocales(): Locale[] {
      return this.locales;
    },
    getDefaultLocale(): Locale | undefined {
      return this.locales.find((locale) => locale.isDefault);
    },
    getSchoolStructure(): SchoolStructure | null {
      return this.currentSchoolStructure;
    },
    getCurrentSchool(): School | null {
      return this.currentSchool;
    },
    getInitialSchoolStructure(): InitialSchoolStructure | null {
      return this.initialSchoolStructure;
    },
    isLoading(): boolean {
      return this.loading;
    },
    getLearningModes(): { label: string; value: string }[] {
      return [
        { label: "Guided", value: "guided" },
        { label: "Assess Ahead", value: "assess-ahead" },
        { label: "Unguided", value: "unguided" },
        { label: "Assess Only (Free)", value: "assess-only-free" },
        { label: "Assess Only (Sequential)", value: "assess-only-sequential" },
      ];
    },
    lingoMode(state): 'v1' | 'v2' | 'both' {
      return state.currentSchool?.lingoMode ?? 'v1'
    },
    /**
     * Is Grammar switched on for the selected school?
     * Rules and rationale live in resolveSchoolGrammarEnabled.
     */
    grammarEnabled(state): boolean {
      const filterStore = useFilterStore();
      return resolveSchoolGrammarEnabled(
        state.currentSchool,
        filterStore.filterOptions?.schoolGroups,
        filterStore.selectedFilters.school
      );
    },
    isEnglishOnlySchool(state): boolean {
      const subjects =
        state.currentSchool?.subjectsAvailable ??
        state.currentSchoolStructure?.subjectsAvailable ??
        [];

      if (!subjects || subjects.length === 0) return false;

      const hasEnglish = subjects.some((subject) => {
        const label = (subject.code || subject.name || "").toUpperCase();
        return label === "ENGLISH";
      });

      const hasMaths = subjects.some((subject) => {
        const label = (subject.code || "").toUpperCase();
        return label === "MATHS" || label === "MATH" || label === "MATHEMATICS";
      });

      return hasEnglish && !hasMaths;
    },
    isMathsOnlySchool(state): boolean {
      const subjects =
        state.currentSchool?.subjectsAvailable ??
        state.currentSchoolStructure?.subjectsAvailable ??
        [];

      if (!subjects || subjects.length === 0) return false;

      const hasEnglish = subjects.some((subject) => {
        const label = (subject.code || subject.name || "").toUpperCase();
        return label === "ENGLISH";
      });

      const hasMaths = subjects.some((subject) => {
        const label = (subject.code || subject.name || "").toUpperCase();
        return label === "MATHS" || label === "MATH" || label === "MATHEMATICS";
      });

      return hasMaths && !hasEnglish;
    },
    hasBothSubjects(state): boolean {
      const subjects =
        state.currentSchool?.subjectsAvailable ??
        state.currentSchoolStructure?.subjectsAvailable ??
        [];

      if (!subjects || subjects.length === 0) return false;

      const hasEnglish = subjects.some((subject) => {
        const label = (subject.code || subject.name || "").toUpperCase();
        return label === "ENGLISH";
      });

      const hasMaths = subjects.some((subject) => {
        const label = (subject.code || subject.name || "").toUpperCase();
        return label === "MATHS" || label === "MATH" || label === "MATHEMATICS";
      });

      return hasEnglish && hasMaths;
    },
  },
  actions: {
    async createSchool(SchoolData: any) {
      try {
        this.schoolCreationLoader = true;
        this.schoolCreationError = "";

        const response = await api.post(
          `v2/organisation/school/new`,
          SchoolData
        );

        if (response.data.success && typeof response.data.data?._id === "string" && response.data.data._id) {
          return response.data.data;
        } else {
          this.schoolCreationError = getSchoolCreationErrorMessage(response.data, response.status);
          return false;
        }
      } catch (error) {
        const response = (error as { response?: { data?: unknown; status?: number } } | null)?.response;
        this.schoolCreationError = getSchoolCreationErrorMessage(response?.data, response?.status);
        return false;
      } finally {
        this.schoolCreationLoader = false;
      }
    },
    async fetchLocales() {
      this.localeLoader = true;
      this.locales = [];

      try {
        const response = await api.get("/v1/locales");

        if (response.status === 200) {
          this.locales = response.data;
        } else {
          throw new Error("Failed to fetch locales.");
        }
      } catch (error) {
        console.error("Error fetching locales: ", error);
        this.locales = [];
      } finally {
        this.localeLoader = false;
      }
    },
    async updateSchool(schoolId: string, schoolDetails: Partial<School>) {
      try {
        this.updateLoader = true;

        // Flatten the payload by spreading location properties into the main payload
        const { location, ...otherData } = schoolDetails;
        const flattenedPayload = {
          ...otherData,
          // Flatten city and country from location into the main payload
          ...location,
        };

        const response = await api.put(
          `/v2/organisation/school/update/${schoolId}`,
          flattenedPayload
        );

        if (response.data.success) {
          this.currentSchool = response.data.data;
          return true;
        } else {
          console.error("Failed to update school:", response.data.message);
          return false;
        }
      } catch (error) {
        console.error("Error updating school:", error);
        return false;
      } finally {
        this.updateLoader = false;
      }
    },
    async validateEmail(email: string) {
      try {
        const emailValidationResponse = await api.get(
          `/v2/access/admin/validate-email?email=${encodeURIComponent(email)}`
        );

        if (emailValidationResponse.status !== 200) {
          throw new Error("Failed to validate email from API.");
        }

        const emailValidationData = emailValidationResponse.data.exists;
        return emailValidationData;
      } catch (error: any) {
        console.log("Error: ", error);
        // if (error.code) {
        //   throw new Error(error.message);
        // }
      }
    },
    // async checkIfNameExists(firstName: string, lastName: string) {
    //   try {
    //     const response = await api.get(
    //       `/v2/access/admin/check-name-exists?firstName=${encodeURIComponent(firstName)}&lastName=${encodeURIComponent(lastName)}`
    //     );
    //     if (response.status === 200) {
    //       return response.data.exists;
    //     } else {
    //       throw new Error("Failed to check if name exists.");
    //     }
    //   } catch (error) {
    //     console.error("Error checking if name exists:", error);
    //     return false;
    //   }
    // },
    updateSchoolChampion(champion: Admin) {
      this.currentSchoolStructure?.schoolChampions.push(champion);
    },
    updateEnableSentModal(value: boolean) {
      this.enableSentModal = value;
    },
    updateClassGroups(classGroups: any) {
      if (this.initialSchoolStructure) {
        this.initialSchoolStructure.classGroups = classGroups;
      }
    },
    updateGrades(grades: any) {
      this.SelectedGrades = grades;
    },
    updateCurrentStep(step: number) {
      this.step = step;
    },
    updateTeachers(data: any) {
      if (this.initialSchoolStructure) {
        this.initialSchoolStructure.teachers = data;
      }
    },
    async fetchSchool(schoolId: string) {
      this.loading = true;
      try {
        const schoolResponse = await api.get(
          `v2/organisation/school/single/${schoolId}`
        );

        if (schoolResponse.status !== 200) {
          throw new Error("Failed to fetch school data from API.");
        }

        const school = schoolResponse.data.data;
        this.currentSchool = school;

        this.setLocale();
      } catch (error: any) {
        console.error("Error fetching school:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async fetchSchoolStructure(schoolId: string) {
      this.schoolStructureLoader = true;
      try {
        const schoolStructureResponse = await api.get(
          `v2/organisation/school/structure/${schoolId}`
        );

        if (schoolStructureResponse.status !== 200) {
          throw new Error("Failed to fetch school data from API.");
        }

        const schoolStructure = schoolStructureResponse.data.data;
        this.currentSchoolStructure = schoolStructure;
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.schoolStructureLoader = false;
      }
    },
    async updateSchoolDetails(
      schoolId: string,
      schoolDetails: Partial<School>
    ) {
      try {
        this.loading = true;

        console.log('updateSchoolDetails', schoolDetails);

        const response = await api.put(
          `/v2/organisation/school/update-structure/${schoolId}`,
          schoolDetails
        );

        if (response.data.success) {
          this.currentSchoolStructure = response.data.data;

          const filterStore = useFilterStore();
          await filterStore.fetchUpdatedFilters();
        } else {
          console.error("Failed to update school:", response.data.message);
        }
      } catch (error) {
        console.error("Error updating school:", error);
      } finally {
        this.loading = false;
      }
    },
    async addNewTeacherToSchool(teacherData: any) {
      try {
        this.loading = true;

        const response = await api.post(
          `/v2/access/admin/initialise-new`,
          teacherData
        );

        if (response.data.success) {
          this.currentSchool = response.data.data;

          const filterStore = useFilterStore();

          const schoolGroupId = this.currentSchoolStructure?.schoolGroup;
          const schoolId = this.currentSchoolStructure?._id;
          const subject = filterStore.selectedSubject;
          const grade = filterStore.selectedGrade;
          const classGroupId = filterStore.selectedClassGroups;

          const dataStore = useDataStore();
          dataStore.fetchInitialData();

          if (schoolGroupId) {
            filterStore.selectSchoolGroup(schoolGroupId);
          }

          if (schoolId) {
            filterStore.selectSchool(schoolId);
          }

          if (subject) {
            filterStore.selectSubject(subject._id);
          }
          
          return response.data.data;
        } else {
          console.error(
            "Failed to add teacher to school:",
            response.data.message
          );
          return response.data.message;
        }
      } catch (error) {
        console.error("Error adding teacher to school:", error);
      } finally {
        this.loading = false;
      }
    },
    async removeClassFromSchool(classGroupId: string) {
      try {
        this.loading = true;
        const response = await api.delete(
          `v2/organisation/class-group/delete/${classGroupId}`
        );
        if (response?.status === 200 && response?.data?.success) {
          const schoolId = this.currentSchoolStructure?._id || this.currentSchool?._id;
          if (schoolId) {
            await this.fetchSchoolStructure(schoolId);
          }
          return {
            success: true,
            message: response.data.message,
          }
        } else {
          return {
            success: false,
            message: response?.data?.message || "Failed to remove class group",
          }
        }
      } catch (error: any) {
        console.log(error);
        return {
          success: false,
          message: error.response?.data?.error?.title + error.response?.data?.error?.body || "Error removing class group",
          error: error
        }
      }
      finally {
        this.loading = false;
      }
    },
    async updateClass(classId: any, updateClassDetails: any) {
      try {
        this.loading = true;

        const response = await api.put(
          `v2/organisation/class-group/update/${classId}`,
          updateClassDetails
        );
        
        if (response.status !== 200) {
          throw new Error("Failed to update the class data from the API.");
        }

        const updateClass = response.data.data;

        if (response.data.success) {
          this.currentSchoolStructure = response.data.data;
          await this.fetchSchoolStructure(updateClass.school);

          const filterStore = useFilterStore();
          await filterStore.fetchUpdatedFilters();
          return updateClass;
        } else {
          console.error("Failed to remove class group:", response.data.message);
        }
      } catch (error) {
        console.error("Error removing class group:", error);
      }
      finally {
        this.loading = false;
      }
    },
    async newClass(newClassDetails: any) {
      try {
        this.loading = true;

        const response = await api.post(
          `v2/organisation/class-group/new`,
          newClassDetails
        );
        
        if (response.status !== 201) {
          throw new Error("Failed to add the class data from the API.");
        }

        const updateClass = response.data.data;

        if (response.data.success) {
          this.currentSchoolStructure = response.data.data;
          await this.fetchSchoolStructure(updateClass.school);

          const filterStore = useFilterStore();
          await filterStore.fetchUpdatedFilters();
          return updateClass;
        } else {
          console.error("Failed to remove class group:", response.data.message);
        }
      } catch (error) {
        console.error("Error removing class group:", error);
      }
      finally {
        this.loading = false;
      }
    },
    async updateTeacherDetails(teacherId: string, teacherDetails: any) {
      try {
        this.loading = true;

        const response = await api.put(
          `/v2/organisation/school-admins/${teacherId}`,
          teacherDetails
        );

        if (response.data.success) {
          this.currentSchool = response.data.data;

          const filterStore = useFilterStore();

          const schoolGroupId = this.currentSchoolStructure?.schoolGroup;
          const schoolId = this.currentSchoolStructure?._id;
          const subject = filterStore.selectedSubject;
          const grade = filterStore.selectedGrade;
          const classGroupId = filterStore.selectedClassGroups;

          const dataStore = useDataStore();
          dataStore.fetchInitialData();

          if (schoolGroupId) {
            filterStore.selectSchoolGroup(schoolGroupId);
          }

          if (schoolId) {
            filterStore.selectSchool(schoolId);
          }

          if (subject) {
            filterStore.selectSubject(subject._id);
          }
          await this.fetchSchoolStructure(teacherDetails.school);
        } else {
          console.error("Failed to update teacher:", response.data.message);
        }
      } catch (error) {
        console.error("Error updating teacher:", error);
      } finally {
        this.loading = false;
      }
    },
    async initialiseSchoolStructure(
      initialStructureData: InitialSchoolStructure
    ) {
      try {
        this.loading = true;

        const response = await api.post(
          `/v2/organisation/school/initialise-structure`,
          initialStructureData
        );
        
        if (response.data.success) {
          this.currentSchoolStructure = response.data.data;
          //set new filters
          const filterStore = useFilterStore();
          await filterStore.fetchUpdatedFilters();
          return true;
        } else {
          console.error(
            "Failed to initialise school structure:",
            response.data.error || response.data.message
          );
          // throw new Error(
          //   response.data.error || response.data.message || "Unknown error"
          // );
          return false;
        }
      } catch (error: any) {
        console.error("Error initialising school structure:", error);
        return false;
        // throw error;
      } finally {
        this.loading = false;
      }
    },
    async archiveOrUnarchiveSchool(schoolId: string, action: "archive" | "unarchive") {
      this.loading = true;
      try {
        const response = await api.post(`/v2/organisation/school/archiving`, {
          schoolId,
          action,
        });
        if (response.status === 200) {
          return response.data;
        } else {
          console.error(`Failed to ${action} school`);
          return false;
        }
      } catch (error) {
        console.error(`Error while trying to ${action} school:`, error);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async updateAdminRoles(adminIds: string[], schoolId: string) {
      this.loading = true;
      try {
        const response = await api.patch('/v2/organisation/school-admins/update-roles', {
          adminIds,
          schoolId,
        });
        if (response.status === 200) {
          return response.data;
        } else {
          console.error('Failed to update admin roles');
          return false;
        }
      } catch (error: any) {
        console.error('Error updating admin roles:', error);
        return false;
      } finally {
        this.loading = false;
      }
    },
    setLocale() {
      const preferredLang = get(this.currentSchool, "preferredLang", "en-ZA");
      
      i18n.global.locale.value = preferredLang as "en-ZA" | "en-GB";
      moment.locale(preferredLang);
    }
  },
  persist: true,
});
